// NovaAI.web.tsx
import React, {useState, useEffect, useMemo, useRef, useCallback} from 'react';
import {
  View,
  TouchableOpacity,
  useWindowDimensions,
  AccessibilityInfo,
} from 'react-native';
import {useScreenTheme} from '../../../shared/hooks/useScreenTheme';
import {createNovaAIWebStyles} from '../../../shared/components/NovaAI/NovaAI.styles.web';
import NovaAIChatWindow from '../../../shared/components/NovaAI/NovaAIChatWindow';
import {useTranslation} from 'react-i18next';
import {useSafeAreaInsets} from 'react-native-safe-area-context';
import Typography from '@shared/constants/Typography';
import {NovaAIFloatingIcon} from '@shared/assets/icons/NovaAIFloatingIcon';

/** Minimum fraction of the chat window that must remain visible on screen
 * when dragged towards any edge (1.0 = window is fully contained within bounds). */
const MIN_VISIBLE_FRACTION = 1.0;

/** Approximate height of the chat window header row (padding24 top+bottom + icon 44px) */
const HEADER_HEIGHT = 92;
/**
 * Fraction of header width used as the drag zone (left side — bot icon + title).
 * The right portion is left uncovered for the Volume/Expand/Close buttons.
 */
const HEADER_DRAG_WIDTH_PCT = '60%';

const MODAL_WIDTH = 549;
const MODAL_HEIGHT = 740;
const BUTTON_SIZE = 72;
const BUTTON_RIGHT = 20;
const BUTTON_BOTTOM = 20;
const VERTICAL_GAP = 12;

/** Right margin for the floating button's default resting position (matches style right:36) */
const FLOATING_BTN_MARGIN = 36;
/** Actual rendered size of the NovaAIFloatingIcon SVG (72×72) */
const FLOATING_BTN_SIZE = 72;
/** Height of the top navigation header (matches MainLayoutScreen container: 100vh - 80px).
 *  The floating button must stay below this so it doesn't overlap the nav header. */
const NAV_HEADER_HEIGHT = 80;
/** Minimum gap between the button edge and the screen edge — button stays fully visible */
const EDGE_PADDING = 16;
/**
 * Snap offset formulae (origin = button's default right:36 position):
 *   snapRight: button's right edge = EDGE_PADDING from screen right
 *     dx = FLOATING_BTN_MARGIN - EDGE_PADDING
 *   snapLeft:  button's left edge  = EDGE_PADDING from screen left
 *     dx = EDGE_PADDING + FLOATING_BTN_MARGIN + FLOATING_BTN_SIZE - screenWidth
 */

const NovaAI = () => {
  const {t} = useTranslation();
  const [modalVisible, setModalVisible] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const {width, height} = useWindowDimensions();
  const theme = useScreenTheme('novaAIScreen');
  const insets = useSafeAreaInsets();

  // ── Chat window drag state ───────────────────────────────────────────────────
  const [dragOffset, setDragOffset] = useState<{x: number; y: number}>({
    x: 0,
    y: 0,
  });
  const isDraggingRef = useRef(false);
  const dragStartRef = useRef({mouseX: 0, mouseY: 0, offsetX: 0, offsetY: 0});

  // ── Floating button drag state ───────────────────────────────────────────────
  const [buttonOffset, setButtonOffset] = useState<{x: number; y: number}>({x: 0, y: 0});
  const isButtonDraggingRef = useRef(false);
  const buttonDragMovedRef = useRef(false);
  const buttonDragStartRef = useRef({mouseX: 0, mouseY: 0, offsetX: 0, offsetY: 0});
  /** Kept current every render so stale-closure handlers always read the latest dimensions. */
  const screenSizeRef = useRef({width, height});
  screenSizeRef.current = {width, height};

  /**
   * Drag bounds ref — updated on every render so the mousemove handler
   * (registered once) always reads the latest screen-size-derived values.
   *
   * The window is allowed to go up to (1 - MIN_VISIBLE_FRACTION) off any edge
   * as long as MIN_VISIBLE_FRACTION of its size stays on screen.
   *
   *   minX:  at least (V*W) visible on right side
   *   maxX:  at least (V*W) visible on left  side
   *   minY:  at least (V*H) visible at bottom
   *   maxY:  at least (V*H) visible at top
   */
  const dragBoundsRef = useRef({minX: 0, maxX: 0, minY: 0, maxY: 0});

  const buttonBottom = Typography.margin.margin36;

  // Responsive constraints — window sits at bottom: 80 in the fixed overlay.
  // Top clearance = NAV_HEADER_HEIGHT (80 px) so the window never overlaps the header.
  const finalWidth = Math.min(MODAL_WIDTH, width - 40);
  const finalHeight = Math.min(MODAL_HEIGHT, height - 80 - NAV_HEADER_HEIGHT);

  /** CSS bottom value used for the chat window (matches shadowWrapper bottom: 80 in styles). */
  const WINDOW_BOTTOM = 80;
  const modalRight = BUTTON_RIGHT;

  const expandedWidth = Math.floor(width / 1.6);
  const expandedHeight = height - insets.top;

  const computedWidth = isExpanded ? expandedWidth : finalWidth;
  const computedHeight = isExpanded
    ? expandedHeight
    : isMinimized
    ? HEADER_HEIGHT
    : finalHeight;
  const computedBottom = isExpanded ? 0 : WINDOW_BOTTOM;
  const computedRight = isExpanded ? 0 : modalRight;

  // Update bounds every render (before effects run) so the stale-closure
  // mousemove handler always reads the correct values via the ref.
  // minY incorporates NAV_HEADER_HEIGHT so the window can never be dragged
  // above the nav header regardless of MIN_VISIBLE_FRACTION.
  dragBoundsRef.current = {
    minX: MIN_VISIBLE_FRACTION * computedWidth - width + computedRight,
    maxX: computedRight + (1 - MIN_VISIBLE_FRACTION) * computedWidth,
    minY:
      NAV_HEADER_HEIGHT +
      MIN_VISIBLE_FRACTION * computedHeight -
      height +
      computedBottom,
    maxY: computedBottom + (1 - MIN_VISIBLE_FRACTION) * computedHeight,
  };
  const styles = useMemo(
    () =>
      createNovaAIWebStyles(
        theme,
        computedWidth,
        computedHeight,
        isExpanded,
      ),
    [
      theme,
      computedWidth,
      computedHeight,
      isExpanded,
    ],
  );

  // ── Drag handlers ───────────────────────────────────────────────────────────
  // onMouseDown is applied to a transparent header-only drag overlay so that
  // only dragging from the header title area (left 60%) initiates a drag.
  // Buttons / inputs in the right portion of the header and the body are
  // deliberately uncovered and receive their own mouse events normally.
  const handleDragStart = useCallback(
    (e: React.MouseEvent) => {
      if (isExpanded) return;
      if (e.button !== 0) return; // only primary (left) mouse button

      e.preventDefault();
      isDraggingRef.current = true;
      dragStartRef.current = {
        mouseX: e.clientX,
        mouseY: e.clientY,
        offsetX: dragOffset.x,
        offsetY: dragOffset.y,
      };
    },
    [isExpanded, dragOffset],
  );

  const handleButtonMouseDown = useCallback((e: React.MouseEvent) => {
    if (e.button !== 0) return;
    isButtonDraggingRef.current = true;
    buttonDragMovedRef.current = false;
    buttonDragStartRef.current = {
      mouseX: e.clientX,
      mouseY: e.clientY,
      offsetX: buttonOffset.x,
      offsetY: buttonOffset.y,
    };
  }, [buttonOffset]);

  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      // Chat window drag
      if (isDraggingRef.current) {
        const dx = e.clientX - dragStartRef.current.mouseX;
        const dy = e.clientY - dragStartRef.current.mouseY;
        const rawX = dragStartRef.current.offsetX + dx;
        const rawY = dragStartRef.current.offsetY + dy;
        const {minX, maxX, minY, maxY} = dragBoundsRef.current;
        setDragOffset({
          x: Math.max(minX, Math.min(maxX, rawX)),
          y: Math.max(minY, Math.min(maxY, rawY)),
        });
      }
      // Floating button drag — clamped so it can't go past the snap boundaries
      if (isButtonDraggingRef.current) {
        const dx = e.clientX - buttonDragStartRef.current.mouseX;
        const dy = e.clientY - buttonDragStartRef.current.mouseY;
        if (Math.abs(dx) > 5 || Math.abs(dy) > 5) {
          buttonDragMovedRef.current = true;
        }
        const {width: sw, height: sh} = screenSizeRef.current;
        const rawX = buttonDragStartRef.current.offsetX + dx;
        const rawY = buttonDragStartRef.current.offsetY + dy;
        // X: clamp so entire button stays inside with EDGE_PADDING from each side
        const minX = EDGE_PADDING + FLOATING_BTN_MARGIN + FLOATING_BTN_SIZE - sw; // left bound
        const maxX = FLOATING_BTN_MARGIN - EDGE_PADDING;                           // right bound
        // Y: clamp — top boundary respects the fixed navigation header (NAV_HEADER_HEIGHT),
        //    bottom boundary respects the bottom tab bar (BTN_BOTTOM).
        const BTN_BOTTOM = 80;
        const minY = -(sh - BTN_BOTTOM - FLOATING_BTN_SIZE - NAV_HEADER_HEIGHT - EDGE_PADDING);
        const maxY = BTN_BOTTOM - EDGE_PADDING;
        setButtonOffset({
          x: Math.max(minX, Math.min(maxX, rawX)),
          y: Math.max(minY, Math.min(maxY, rawY)),
        });
      }
    };
    const onUp = (e: MouseEvent) => {
      isDraggingRef.current = false;
      if (isButtonDraggingRef.current) {
        isButtonDraggingRef.current = false;
        if (buttonDragMovedRef.current) {
          // Snap to nearest horizontal side, keep vertical position (clamped)
          const {width: sw, height: sh} = screenSizeRef.current;
          const finalOffsetX =
            buttonDragStartRef.current.offsetX +
            (e.clientX - buttonDragStartRef.current.mouseX);
          const rawOffsetY =
            buttonDragStartRef.current.offsetY +
            (e.clientY - buttonDragStartRef.current.mouseY);
          // Clamp Y — top boundary is below the fixed navigation header, bottom is above the tab bar
          const BTN_BOTTOM = 80;
          const minY = -(sh - BTN_BOTTOM - FLOATING_BTN_SIZE - NAV_HEADER_HEIGHT - EDGE_PADDING);
          const maxY = BTN_BOTTOM - EDGE_PADDING;
          const finalOffsetY = Math.max(minY, Math.min(maxY, rawOffsetY));
          const buttonCenterX =
            sw - FLOATING_BTN_MARGIN - FLOATING_BTN_SIZE / 2 + finalOffsetX;
          const snapLeft = buttonCenterX < sw / 2;
          // Snap fully inside: EDGE_PADDING gap from whichever edge it lands near
          const snapX = snapLeft
            ? EDGE_PADDING + FLOATING_BTN_MARGIN + FLOATING_BTN_SIZE - sw  // left snap
            : FLOATING_BTN_MARGIN - EDGE_PADDING;                           // right snap
          setButtonOffset({x: snapX, y: finalOffsetY});
        }
      }
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    return () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
  }, []);

  const handleClose = useCallback(() => {
    setModalVisible(false);
    setIsExpanded(false);
    setIsMinimized(false);
    setDragOffset({x: 0, y: 0});
  }, []);

  const handleToggleExpand = useCallback(() => {
    setIsExpanded(prev => !prev);
    setDragOffset({x: 0, y: 0});
  }, []);

  useEffect(() => {
    if (modalVisible) {
      AccessibilityInfo.announceForAccessibility('Nova AI chat opened');
    } else {
      AccessibilityInfo.announceForAccessibility('Nova AI chat closed');
    }
  }, [modalVisible]);

  // Translate the shadowWrapper to create the drag offset — the window's
  // natural bottom/right anchor is preserved so no stylesheet values need
  // to be overridden.
  const dragTransformStyle: React.CSSProperties | undefined =
    !isExpanded && (dragOffset.x !== 0 || dragOffset.y !== 0)
      ? {
          transform: `translate(${dragOffset.x}px, ${dragOffset.y}px)`,
          userSelect: 'none',
        }
      : undefined;

  return (
    <>
      {/* Floating Button — draggable, snaps to nearest side on release */}
      {!modalVisible ? (
        <TouchableOpacity
          style={[
            styles.floatingButton,
            (() => {
              // Hard-clamp at render time so the button can NEVER visually escape its
              // boundary regardless of the state value (e.g. race conditions, stale refs).
              const {width: sw, height: sh} = screenSizeRef.current;
              const BTN_BOTTOM = 80;
              const minY = -(sh - BTN_BOTTOM - FLOATING_BTN_SIZE - NAV_HEADER_HEIGHT - EDGE_PADDING);
              const maxY = BTN_BOTTOM - EDGE_PADDING;
              const minX = EDGE_PADDING + FLOATING_BTN_MARGIN + FLOATING_BTN_SIZE - sw;
              const maxX = FLOATING_BTN_MARGIN - EDGE_PADDING;
              const cx = Math.max(minX, Math.min(maxX, buttonOffset.x));
              const cy = Math.max(minY, Math.min(maxY, buttonOffset.y));
              return (cx !== 0 || cy !== 0)
                ? {transform: [{translateX: cx}, {translateY: cy}] as any}
                : undefined;
            })(),
            // @ts-ignore — web-only CSS cursor
            {cursor: isButtonDraggingRef.current ? 'grabbing' : 'grab'} as any,
          ]}
          onPress={() => {
            // Only open modal on a genuine tap (not end of drag)
            if (!buttonDragMovedRef.current) {
              setModalVisible(true);
            }
            buttonDragMovedRef.current = false;
          }}
          // @ts-ignore — React Native Web accepts onMouseDown on TouchableOpacity
          onMouseDown={handleButtonMouseDown}
          testID="novaAIFloatingIconId"
          accessible={true}
          accessibilityRole="button"
          accessibilityLabel={t('novaAI.accessibility.floatingButtonLabel')}
          accessibilityHint={t('novaAI.accessibility.floatingButtonHint')}>
          <NovaAIFloatingIcon color={theme.novaAIIconBGColor} />
        </TouchableOpacity>
      ) : (
        <View style={styles.overlay}>
          <View style={[styles.shadowWrapper, dragTransformStyle as any]}>
            <View
              style={styles.modalView}
              accessible={true}
              accessibilityRole="none"
              accessibilityLabel={t('novaAI.accessibility.chatWindowLabel')}>
              <NovaAIChatWindow
                onClose={handleClose}
                visible={modalVisible}
                isExpanded={isExpanded}
                onToggleExpand={handleToggleExpand}
                isMinimized={isMinimized}
                onToggleMinimize={() => {
                  if (!isMinimized && isExpanded) {
                    setIsExpanded(false);
                  }
                  setIsMinimized(prev => !prev);
                }}
              />
            </View>

            {/* ── Header drag zone (rendered AFTER = on top in z-order) ───
                A transparent overlay covers only the left portion of the
                header row (bot icon + "Nova AI" title area).
                The right portion (Volume/Expand/Close) and the entire body
                are uncovered so those controls work normally.            */}
            {!isExpanded && (
              <View
                // @ts-ignore — React Native Web accepts onMouseDown on View
                onMouseDown={handleDragStart}
                style={[
                  {
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: HEADER_DRAG_WIDTH_PCT,
                    height: HEADER_HEIGHT,
                    zIndex: 10,
                  },
                  // @ts-ignore — web-only CSS cursor property
                  {cursor: 'grab'} as any,
                ]}
              />
            )}
          </View>
        </View>
      )}
    </>
  );
};

export default NovaAI;
